import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
  type Control,
  Controller,
  type UseFormSetValue,
} from "react-hook-form";

import useDevMode from "../../../utils/devmode";
import { ErrorHelperText } from "../../PatientsDashboard/ui/ErrorHelper";
import { CREDENTIAL_TYPE, typeFromCredential } from "../common";
import { InputLabelText } from "../ui/InputLabelText";
import { InputWrapper } from "../ui/InputWrapper";
import { LineBreak } from "../ui/LineBreak";
import { MfaFields } from "./MfaFields";
import type {
  AddPayerCredentialsFormFields,
  UpdatePayerCredentialsFormFields,
} from "./PayerCredentialsForm.zod";
import { UserFields } from "./UserFields";

interface AddPayerCredentialFormFieldProps {
  control: Control<UpdatePayerCredentialsFormFields>;
  setValue: UseFormSetValue<UpdatePayerCredentialsFormFields>;
  currentCredentials: Partial<UserCredentialsMessage>;
  supportedSourcesMap: Record<string, SourceMessage>;
}

export function UpdatePayerCredentialFormFields({
  control,
  setValue,
  currentCredentials,
  supportedSourcesMap,
}: AddPayerCredentialFormFieldProps) {
  const devMode = useDevMode();
  const { sourceId } = currentCredentials;
  const selectedPayer = sourceId ? supportedSourcesMap[sourceId] : null;
  const credentialType = typeFromCredential(currentCredentials);
  const isOrtho2 = currentCredentials.pmsDisplayName === "Ortho2 Edge";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "0",
        padding: "1rem 0",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          width: "100%",
          justifyContent: "space-between",
          marginBottom: "1rem",
        }}
      >
        <InputLabelText>Credential</InputLabelText>

        {credentialType === CREDENTIAL_TYPE.PAYER && (
          <Typography sx={{ marginRight: "auto", color: "#475467" }}>
            {currentCredentials.sourceName ?? currentCredentials.name}
          </Typography>
        )}
        {credentialType === CREDENTIAL_TYPE.PMS && (
          <Typography sx={{ marginRight: "auto", color: "#475467" }}>
            {currentCredentials.pmsDisplayName}
          </Typography>
        )}
      </Box>

      <Controller
        name="username"
        defaultValue={currentCredentials.username}
        control={control}
        render={({
          field: { ref, name, value, onBlur, onChange },
          fieldState: { error },
        }) => (
          <FormControl
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              borderRadius: "8px",
            }}
          >
            <FormLabel>
              <InputLabelText>Username</InputLabelText>
            </FormLabel>
            <InputWrapper>
              <TextField
                name={name}
                value={value}
                type="text"
                variant="outlined"
                placeholder="Update Username"
                ref={ref}
                onChange={onChange}
                onBlur={onBlur}
                inputProps={{
                  "data-testid": "usernameInput",
                  autoComplete: "username",
                  sx: {
                    border: "none",
                  },
                }}
                sx={{
                  backgroundColor: "#F9FAFB",
                  width: "100%",
                  fontSize: "1rem",
                  ".MuiInputBase-root": {
                    borderRadius: "8px",
                    border: "none",
                  },
                  ".MuiOutlinedInput-notchedOutline": {
                    border: "solid 1px rgba(208, 213, 221, 0.5)",
                  },
                }}
              />
              <ErrorHelperText message={error?.message} />
            </InputWrapper>
          </FormControl>
        )}
      />

      {credentialType === CREDENTIAL_TYPE.PMS && isOrtho2 && (
        <>
          <Controller
            name="activationCode"
            defaultValue={currentCredentials.activationCode}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <FormControl
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "center",
                  alignItems: "center",
                  borderRadius: "8px",
                }}
              >
                <FormLabel>
                  <InputLabelText>Activation Code</InputLabelText>
                </FormLabel>
                <InputWrapper>
                  <TextField
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    name={field.name}
                    inputRef={field.ref}
                    type="text"
                    variant="outlined"
                    placeholder="Enter Activation Code"
                    error={!!error}
                    inputProps={{
                      "data-testid": "activationCodeInput",
                      autoComplete: "off",
                      sx: {
                        border: "none",
                      },
                    }}
                    sx={{
                      backgroundColor: "#F9FAFB",
                      width: "100%",
                      fontSize: "1rem",
                      ".MuiInputBase-root": {
                        borderRadius: "8px",
                        border: "none",
                      },
                      ".MuiOutlinedInput-notchedOutline": {
                        border: "solid 1px rgba(208, 213, 221, 0.5)",
                      },
                    }}
                  />
                  <ErrorHelperText message={error?.message} />
                </InputWrapper>
              </FormControl>
            )}
          />
          <Controller
            name="serialNumber"
            defaultValue={currentCredentials.serialNumber}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <FormControl
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "center",
                  alignItems: "center",
                  borderRadius: "8px",
                }}
              >
                <FormLabel>
                  <InputLabelText>Serial Number</InputLabelText>
                </FormLabel>
                <InputWrapper>
                  <TextField
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    name={field.name}
                    inputRef={field.ref}
                    type="text"
                    variant="outlined"
                    placeholder="Enter Serial Number"
                    error={!!error}
                    inputProps={{
                      "data-testid": "serialNumberInput",
                      autoComplete: "off",
                      sx: {
                        border: "none",
                      },
                    }}
                    sx={{
                      backgroundColor: "#F9FAFB",
                      width: "100%",
                      fontSize: "1rem",
                      ".MuiInputBase-root": {
                        borderRadius: "8px",
                        border: "none",
                      },
                      ".MuiOutlinedInput-notchedOutline": {
                        border: "solid 1px rgba(208, 213, 221, 0.5)",
                      },
                    }}
                  />
                  <ErrorHelperText message={error?.message} />
                </InputWrapper>
              </FormControl>
            )}
          />
          <Controller
            name="customerNumber"
            defaultValue={currentCredentials.customerNumber}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <FormControl
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "center",
                  alignItems: "center",
                  borderRadius: "8px",
                }}
              >
                <FormLabel>
                  <InputLabelText>Customer Number</InputLabelText>
                </FormLabel>
                <InputWrapper>
                  <TextField
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    name={field.name}
                    inputRef={field.ref}
                    type="text"
                    variant="outlined"
                    placeholder="Enter Customer Number"
                    error={!!error}
                    inputProps={{
                      "data-testid": "customerNumberInput",
                      autoComplete: "off",
                      sx: {
                        border: "none",
                      },
                    }}
                    sx={{
                      backgroundColor: "#F9FAFB",
                      width: "100%",
                      fontSize: "1rem",
                      ".MuiInputBase-root": {
                        borderRadius: "8px",
                        border: "none",
                      },
                      ".MuiOutlinedInput-notchedOutline": {
                        border: "solid 1px rgba(208, 213, 221, 0.5)",
                      },
                    }}
                  />
                  <ErrorHelperText message={error?.message} />
                </InputWrapper>
              </FormControl>
            )}
          />
          <Controller
            name="machineIdentifier"
            defaultValue={currentCredentials.machineIdentifier}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <FormControl
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "center",
                  alignItems: "center",
                  borderRadius: "8px",
                }}
              >
                <FormLabel>
                  <InputLabelText>Machine ID</InputLabelText>
                </FormLabel>
                <InputWrapper>
                  <TextField
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    name={field.name}
                    inputRef={field.ref}
                    type="text"
                    variant="outlined"
                    placeholder="Enter Machine ID"
                    error={!!error}
                    inputProps={{
                      "data-testid": "machineIdentifierInput",
                      autoComplete: "off",
                      sx: {
                        border: "none",
                      },
                    }}
                    sx={{
                      backgroundColor: "#F9FAFB",
                      width: "100%",
                      fontSize: "1rem",
                      ".MuiInputBase-root": {
                        borderRadius: "8px",
                        border: "none",
                      },
                      ".MuiOutlinedInput-notchedOutline": {
                        border: "solid 1px rgba(208, 213, 221, 0.5)",
                      },
                    }}
                  />
                  <ErrorHelperText message={error?.message} />
                </InputWrapper>
              </FormControl>
            )}
          />
        </>
      )}

      <Controller
        name="website"
        defaultValue={currentCredentials.website ?? undefined}
        control={control}
        render={({
          field: { ref, name, value, onBlur, onChange },
          fieldState: { error },
        }) => (
          <FormControl
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              borderRadius: "8px",
            }}
          >
            <FormLabel>
              <InputLabelText>Website</InputLabelText>
            </FormLabel>
            <InputWrapper>
              <TextField
                disabled={!currentCredentials.website && !!selectedPayer}
                name={name}
                value={value}
                type="text"
                variant="outlined"
                placeholder={selectedPayer?.website || "Update Website"}
                ref={ref}
                onChange={onChange}
                onBlur={onBlur}
                inputProps={{
                  "data-testid": "labelInput",
                  autoComplete: "off",
                  sx: {
                    border: "none",
                  },
                }}
                sx={{
                  backgroundColor: "#F9FAFB",
                  width: "100%",
                  margin: "0 auto",
                  fontSize: "1rem",
                  maxWidth: "785px",
                  ".MuiInputBase-root": {
                    borderRadius: "8px",
                    border: "none",
                  },
                  ".MuiOutlinedInput-notchedOutline": {
                    border: "solid 1px rgba(208, 213, 221, 0.5)",
                  },
                  "& .MuiInputBase-input::placeholder": {
                    opacity: currentCredentials?.website ? 1 : "none",
                  },
                }}
              />
              <ErrorHelperText message={error?.message} />
            </InputWrapper>
          </FormControl>
        )}
      />
      {credentialType === CREDENTIAL_TYPE.PAYER && (
        <Controller
          name="notes"
          defaultValue={currentCredentials.notes ?? undefined}
          control={control}
          render={({
            field: { ref, name, value, onBlur, onChange },
            fieldState: { error },
          }) => (
            <FormControl
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                alignItems: "center",
                borderRadius: "8px",
              }}
            >
              <FormLabel>
                <InputLabelText>Notes</InputLabelText>
              </FormLabel>
              <InputWrapper>
                <TextField
                  multiline
                  name={name}
                  value={value}
                  rows={4}
                  type="text"
                  variant="outlined"
                  placeholder="Update Notes"
                  ref={ref}
                  onChange={onChange}
                  onBlur={onBlur}
                  inputProps={{
                    "data-testid": "labelInput",
                    autoComplete: "off",
                    sx: {
                      border: "none",
                    },
                  }}
                  sx={{
                    backgroundColor: "#F9FAFB",
                    width: "100%",
                    margin: "0 auto",
                    fontSize: "1rem",
                    maxWidth: "785px",
                    ".MuiInputBase-root": {
                      borderRadius: "8px",
                      border: "none",
                    },
                    ".MuiOutlinedInput-notchedOutline": {
                      border: "solid 1px rgba(208, 213, 221, 0.5)",
                    },
                    // "& .MuiInputBase-root": {
                    //   height: "10rem",
                    // },
                  }}
                />
                <ErrorHelperText message={error?.message} />
              </InputWrapper>
            </FormControl>
          )}
        />
      )}

      {devMode && (
        <MfaFields
          control={control as unknown as Control<AddPayerCredentialsFormFields>}
          setValue={
            setValue as unknown as UseFormSetValue<AddPayerCredentialsFormFields>
          }
          existingMfaJson={currentCredentials.mfaJson}
        />
      )}

      <UserFields
        control={control}
        setValue={setValue}
        initialUserType={currentCredentials.userType}
        initialUserId={currentCredentials.userId ?? undefined}
        readOnly
      />

      <LineBreak />

      <Controller
        name="password"
        defaultValue={currentCredentials.password}
        control={control}
        render={({
          field: { ref, name, value, onBlur, onChange },
          fieldState: { error },
        }) => (
          <FormControl
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              borderRadius: "8px",
              marginTop: "1rem",
            }}
          >
            <FormLabel>
              <InputLabelText>Password</InputLabelText>
            </FormLabel>
            <InputWrapper>
              <TextField
                name={name}
                value={value}
                type="password"
                variant="outlined"
                placeholder="Enter Password"
                ref={ref}
                onChange={onChange}
                onBlur={onBlur}
                inputProps={{
                  "data-testid": "passwordInput",
                  autoComplete: "current-password",
                }}
                sx={{
                  backgroundColor: "#F9FAFB",
                  width: "100%",
                  margin: "0 auto",
                  fontSize: "1rem",
                  maxWidth: "785px",
                  ".MuiInputBase-root": {
                    borderRadius: "8px",
                  },
                  ".MuiOutlinedInput-notchedOutline": {
                    border: "solid 1px rgba(208, 213, 221, 0.5)",
                  },
                }}
              />
              <ErrorHelperText message={error?.message} />
            </InputWrapper>
          </FormControl>
        )}
      />
    </Box>
  );
}
